'use client';

import { Toast } from '@base-ui/react';
import { AlertCircle, Check, Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';

export const navigationToastManager = Toast.createToastManager();
let pendingNavigationToastId: string | null = null;

export function showNavigationToast(message: string) {
  // A navigation banner always replaces the previous one; leaving the old toast
  // to expire on its own timeout is what makes them pile up on screen.
  hideNavigationToast();
  pendingNavigationToastId = navigationToastManager.add({
    title: message,
    type: 'info',
    priority: 'high',
    timeout: 10_000,
  });
}

export function hideNavigationToast() {
  if (pendingNavigationToastId) {
    navigationToastManager.close(pendingNavigationToastId);
    pendingNavigationToastId = null;
  }
}

function NavigationToastList() {
  const { toasts } = Toast.useToastManager();
  return (
    <Toast.Viewport className="pointer-events-none fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 flex-col gap-2">
      {toasts.map((toast) => (
        <Toast.Root
          key={toast.id}
          toast={toast}
          className={cn(
            'flex items-center gap-3 rounded-2xl',
            'bg-gray-900 px-4 py-3 shadow-lg',
            'transition-all duration-200',
            'data-[starting-style]:translate-y-2 data-[starting-style]:opacity-0',
            'data-[ending-style]:translate-y-2 data-[ending-style]:opacity-0',
          )}
        >
          <Loader2 className="size-4 shrink-0 animate-spin text-blue-400" />
          <Toast.Description className="whitespace-nowrap text-sm text-white">
            {toast.title}
          </Toast.Description>
        </Toast.Root>
      ))}
    </Toast.Viewport>
  );
}

export function NavigationToastProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <Toast.Provider toastManager={navigationToastManager} limit={1}>
      {children}
      <NavigationToastList />
    </Toast.Provider>
  );
}

export const errorToastManager = Toast.createToastManager();

export function showErrorToast(message: string) {
  errorToastManager.add({
    title: message,
    type: 'error',
    priority: 'high',
    timeout: 5_000,
  });
}

function ErrorToastList() {
  const { toasts } = Toast.useToastManager();
  return (
    <Toast.Viewport className="pointer-events-none fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 flex-col gap-2">
      {toasts.map((toast) => (
        <Toast.Root
          key={toast.id}
          toast={toast}
          className={cn(
            'flex items-center gap-3 rounded-2xl',
            'bg-gray-900 px-4 py-3 shadow-lg',
            'transition-all duration-200',
            'data-[starting-style]:translate-y-2 data-[starting-style]:opacity-0',
            'data-[ending-style]:translate-y-2 data-[ending-style]:opacity-0',
          )}
        >
          <AlertCircle className="size-4 shrink-0 text-red-400" />
          <Toast.Description className="whitespace-nowrap text-sm text-white">
            {toast.title}
          </Toast.Description>
        </Toast.Root>
      ))}
    </Toast.Viewport>
  );
}

export function ErrorToastProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <Toast.Provider toastManager={errorToastManager} limit={3}>
      {children}
      <ErrorToastList />
    </Toast.Provider>
  );
}

export const copiedToastManager = Toast.createToastManager();

export function showCopiedToast(message: string) {
  copiedToastManager.add({
    title: message,
    type: 'info',
    priority: 'high',
    timeout: 2_000,
  });
}

function CopiedToastList() {
  const { toasts } = Toast.useToastManager();
  return (
    <Toast.Viewport className="pointer-events-none fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 flex-col gap-2">
      {toasts.map((toast) => (
        <Toast.Root
          key={toast.id}
          toast={toast}
          className={cn(
            'flex items-center gap-3 rounded-2xl',
            'bg-gray-900 px-4 py-3 shadow-lg',
            'transition-all duration-200',
            'data-[starting-style]:translate-y-2 data-[starting-style]:opacity-0',
            'data-[ending-style]:translate-y-2 data-[ending-style]:opacity-0',
          )}
        >
          <Check className="size-4 shrink-0 text-green-400" />
          <Toast.Description className="whitespace-nowrap text-sm text-white">
            {toast.title}
          </Toast.Description>
        </Toast.Root>
      ))}
    </Toast.Viewport>
  );
}

export function CopiedToastProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <Toast.Provider toastManager={copiedToastManager} limit={1}>
      {children}
      <CopiedToastList />
    </Toast.Provider>
  );
}
